import { useEffect } from 'react';
import { useStoreSettings } from '../hooks/useStoreSettings';

// Darken a hex color by mixing in black. Returns a hex string.
const darken = (hex: string, amount = 0.12): string => {
  const h = hex.replace('#', '');
  if (h.length !== 6 && h.length !== 8) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const mix = (c: number) => Math.max(0, Math.round(c * (1 - amount)));
  const to2 = (n: number) => n.toString(16).padStart(2, '0');
  return `#${to2(mix(r))}${to2(mix(g))}${to2(mix(b))}`;
};

// Perceptual luminance (sRGB). Used to pick a readable foreground color
// on top of the accent — white text on light yellow is unreadable.
const luminance = (hex: string): number => {
  const h = hex.replace('#', '');
  if (h.length !== 6) return 0;
  const toLin = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = toLin(parseInt(h.slice(0, 2), 16));
  const g = toLin(parseInt(h.slice(2, 4), 16));
  const b = toLin(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { settings } = useStoreSettings();
  const color = (settings as any)?.theme_color || '#ff4e00';
  const buttonTextOverride = (settings as any)?.theme_button_text_color || null;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent-color', color);
    root.style.setProperty('--accent-color-hover', darken(color, 0.12));
    // Darker shade for badges that need ≥4.5:1 contrast against white text.
    root.style.setProperty('--accent-color-dark', darken(color, 0.28));
    // Explicit override wins; otherwise pick white on dark, dark grey on light.
    const fg = buttonTextOverride || (luminance(color) > 0.45 ? '#1f2937' : '#ffffff');
    root.style.setProperty('--accent-fg', fg);
  }, [color, buttonTextOverride]);

  return <>{children}</>;
};

export default ThemeProvider;
